import { GetWSMgr, ReRenderCanvas } from "@/Utils/StateLib";
import type { OpenCtrlRef } from "@theorgchartdev/storybook";
import { UpdateBranchStyle } from "@/appComponents/Navigation/ContextualToolbar/BranchToolsComposite/BranchToolsComposite";
import {
  ExecuteBoxFormatCommand,
  type BoxFormatCommand
} from "@coreapp/utils/ExecuteBoxFormatCommand";
import { enumBoxStyleSet } from "@coreapp/constants";
import { appstr } from "@coreapp/utils/appstr";
import { SaveBoxStyles } from "@/Utils/Promises";
import { TOAST_STATUS } from "@/providers/ToastProvider";
import { createBoldToastMessage } from "@/appComponents/Blocks/Cards/ToastContainer/utils";

import { GetToolbarTargetBox } from "@coreapp/utils/handleMouseUp";

type AddToast = (toast: Record<string, unknown>) => void;

// A toolbar opened from a selected cell acts on the parent box, selected the way right-click selects a box.
const selectToolbarTargetBox = () => {
  const wsMgr = GetWSMgr();
  const cp = wsMgr?.fCanvasPanel;
  if (!wsMgr || !cp) {
    return null;
  }

  const box = GetToolbarTargetBox(cp);
  if (box && !box.IsSelected()) {
    wsMgr.SetToolMode("ToolMode1", true);
    cp.DoSelectElement(box, true);
  }
  return box;
};

export const handleCanvasClickCommand = (openCtrlRef: React.RefObject<OpenCtrlRef | null>) => {
  return (cmd: string) => () => {
    openCtrlRef.current?.setOpen(false);

    const wsMgr = GetWSMgr();
    if (!wsMgr) {
      return;
    }

    const cp = wsMgr.fCanvasPanel;
    if (!cp) {
      return;
    }

    const chartBox = selectToolbarTargetBox();
    const aCmd = wsMgr.AssembleCommand(chartBox, cp, `CanvasRightClick_${cmd}`);
    if (!aCmd) {
      return;
    }

    (aCmd as any)["newvalue"] = cmd;
    wsMgr.HandleRightClickCmd("CanvasRightClick", aCmd);
    ReRenderCanvas();
  };
};

export const handleBoxFormatCommand = (openCtrlRef: React.RefObject<OpenCtrlRef | null>) => {
  return (cmd: BoxFormatCommand) => () => {
    openCtrlRef.current?.setOpen(false);

    const wsMgr = GetWSMgr();
    const chartBox = selectToolbarTargetBox();
    if (!wsMgr || !chartBox) {
      return;
    }

    ExecuteBoxFormatCommand(wsMgr, chartBox, cmd);
    ReRenderCanvas();
  };
};

export const handleAddBoxToLibrary = (
  openCtrlRef: React.RefObject<OpenCtrlRef | null>,
  addToast: AddToast
) => {
  return async () => {
    openCtrlRef.current?.setOpen(false);

    const wsMgr = GetWSMgr();
    const doc = wsMgr?.GetDoc();
    const bsMgr = wsMgr?.GetBoxStylesMgr();
    const source = selectToolbarTargetBox()?.GetLayout();
    if (!wsMgr || !doc || !bsMgr || !source) {
      return;
    }

    const inSharedSet = <T>(action: () => T): T => {
      const priorSet = bsMgr.CurrentBoxStyleSet();
      bsMgr.SwitchBoxStyles(enumBoxStyleSet.sharedboxstyles);
      try {
        return action();
      } finally {
        bsMgr.SwitchBoxStyles(priorSet);
      }
    };

    let addedLayout = null;
    try {
      const newLayout = source.Clone();
      newLayout.SetIsBoxStyle(true);
      // SetIsBoxStyle nulled the cells' live field refs; rebind them so the copy isn't blank.
      doc.FinishLoadLayout(newLayout);
      addedLayout = inSharedSet(() =>
        bsMgr.AddLayout(newLayout, source.GetBoxStyleName() + " copy")
      );
      if (!addedLayout) {
        throw new Error("Failed to add box style to library");
      }
      // SaveBoxStyles serializes the active set synchronously, before its first await.
      await inSharedSet(() => SaveBoxStyles());
      inSharedSet(() => wsMgr.BoxStylesSaved());
      wsMgr.GetUsageTracker()?.ReportEvent("box_layout_editor.box_style.created");
      addToast({
        ...createBoldToastMessage("copied to box style library", {
          "box style name": addedLayout.GetBoxStyleName()
        }),
        status: TOAST_STATUS.SUCCESS
      });
    } catch {
      const layoutToRemove = addedLayout;
      if (layoutToRemove) {
        inSharedSet(() => bsMgr.RemoveLayout(layoutToRemove));
      }
      addToast({
        message: appstr("The box style could not be copied"),
        status: TOAST_STATUS.ERROR
      });
    }
  };
};

export const handleBranchStyleChange = (openCtrlRef: React.RefObject<OpenCtrlRef | null>) => {
  return (target: { value: string }) => {
    openCtrlRef.current?.setOpen(false);
    selectToolbarTargetBox();
    UpdateBranchStyle(target);
    ReRenderCanvas();
  };
};
